"use client"

import { useQuery } from "@tanstack/react-query"

import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import { getWorkQueueSummary, listWorkQueueItems } from "@/features/work-queue/services/work-queue-api"
import { workQueueQueryKeys } from "@/features/work-queue/services/work-queue-query-keys"
import type { WorkQueueItemType } from "@/features/work-queue/model/work-queue.types"
import { useFeatureAccess } from "@/shared/feature-flags"

const WORK_QUEUE_POLL_INTERVAL_MS = 60_000

/** Header badge: total pending actionable items for the current user (across all types they may act on). */
export function useWorkQueueSummaryQuery() {
  const workspaceId = useAuthContextStore((state) => state.activeWorkspaceId)
  const userId = useAuthContextStore((state) => state.authMe?.user.id)
  // Work Queue is PAUSED for V1 and its API module is gated, so this endpoint 403s. The query polls on an
  // interval and refetches on focus, so leaving it enabled would hammer a forbidden endpoint forever and fill
  // the console with errors, the gate has to be here, not only on the thing that renders the count.
  const workQueueAllowed = useFeatureAccess("workQueue.view").allowed

  return useQuery({
    queryKey: workQueueQueryKeys.summary(workspaceId, userId),
    queryFn: () => getWorkQueueSummary(workspaceId ?? ""),
    enabled: workQueueAllowed && Boolean(workspaceId) && Boolean(userId),
    refetchOnWindowFocus: true,
    refetchInterval: WORK_QUEUE_POLL_INTERVAL_MS,
  })
}

/**
 * The summary narrowed to ONE branch, for the branch home dashboard. The server applies every item type's own
 * authorization, so this never shows the caller anything they could not act on.
 */
export function useWorkQueueBranchSummaryQuery(branchId: string | null | undefined, options: { enabled?: boolean } = {}) {
  const workspaceId = useAuthContextStore((state) => state.activeWorkspaceId)
  const userId = useAuthContextStore((state) => state.authMe?.user.id)
  const workQueueAllowed = useFeatureAccess("workQueue.view").allowed

  return useQuery({
    queryKey: workQueueQueryKeys.branchSummary(workspaceId, userId, branchId),
    queryFn: () => getWorkQueueSummary(workspaceId ?? "", branchId ?? undefined),
    enabled: (options.enabled ?? true) && workQueueAllowed && Boolean(workspaceId) && Boolean(userId) && Boolean(branchId),
    refetchOnWindowFocus: true,
    refetchInterval: WORK_QUEUE_POLL_INTERVAL_MS,
  })
}

/**
 * One page of the unified, permission-filtered Work Queue, optionally narrowed to one item type. The previous page
 * of the SAME tab stays on screen while the next one loads, so paging does not flash a skeleton; another tab's
 * rows never do, since its columns would not fit them.
 */
export function useWorkQueueItemsQuery(type: WorkQueueItemType | null, paging: { page: number; limit: number }) {
  const workspaceId = useAuthContextStore((state) => state.activeWorkspaceId)
  const userId = useAuthContextStore((state) => state.authMe?.user.id)

  return useQuery({
    queryKey: workQueueQueryKeys.items(workspaceId, userId, type, paging),
    queryFn: () => listWorkQueueItems(workspaceId ?? "", { type: type ?? undefined, page: paging.page, limit: paging.limit }),
    enabled: Boolean(workspaceId) && Boolean(userId),
    placeholderData: (previous, previousQuery) =>
      previousQuery && previousQuery.queryKey[4] === (type ?? "__all__") ? previous : undefined,
    refetchOnWindowFocus: true,
  })
}
